export type StaleReadMeta = {
  stale: boolean;
  lastSuccessfulAt: number | null;
  fallbackSource: 'exact' | 'family' | null;
  matchesCurrentQuery: boolean;
  matchesCurrentFilters: boolean;
  refreshFailed: boolean;
};

type CacheEntry<T> = {
  data: T;
  lastSuccessfulAt: number;
};

const exactReadCache = new Map<string, CacheEntry<unknown>>();
const familyReadCache = new Map<string, CacheEntry<unknown>>();

const now = () => Date.now();

const buildStaleReadMeta = (
  fallbackSource: 'exact' | 'family',
  matchesCurrentFilters: boolean,
  lastSuccessfulAt: number
): StaleReadMeta => ({
  stale: true,
  lastSuccessfulAt,
  fallbackSource,
  matchesCurrentQuery: fallbackSource === 'exact',
  matchesCurrentFilters,
  refreshFailed: true,
});

export const resetStaleReadCache = (): void => {
  exactReadCache.clear();
  familyReadCache.clear();
};

export const recordSuccessfulRead = <T>(exactKey: string, familyKey: string, data: T): void => {
  const entry: CacheEntry<T> = {
    data,
    lastSuccessfulAt: now(),
  };

  exactReadCache.set(exactKey, entry);
  familyReadCache.set(familyKey, entry);
};

export const clearSuccessfulRead = (exactKey: string, familyKey: string): void => {
  exactReadCache.delete(exactKey);
  familyReadCache.delete(familyKey);
};

export const resolveStaleReadFallback = <T>(
  exactKey: string,
  familyKey: string,
  familyMatchesCurrentFilters: boolean
): { data: T; staleRead: StaleReadMeta } | null => {
  const exactEntry = exactReadCache.get(exactKey) as CacheEntry<T> | undefined;
  if (exactEntry) {
    return {
      data: exactEntry.data,
      staleRead: buildStaleReadMeta('exact', true, exactEntry.lastSuccessfulAt),
    };
  }

  const familyEntry = familyReadCache.get(familyKey) as CacheEntry<T> | undefined;
  if (familyEntry) {
    return {
      data: familyEntry.data,
      staleRead: buildStaleReadMeta(
        'family',
        familyMatchesCurrentFilters,
        familyEntry.lastSuccessfulAt
      ),
    };
  }

  return null;
};
